import React, { useEffect, useMemo } from 'react';
import {
    Box, TextField, Button, FormControlLabel, Checkbox, Chip, Stack, Grid, CircularProgress, Typography
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Clear as ClearIcon } from '@mui/icons-material';
import useFetch from '../../../../hooks/useFetch';

const FilterBox = styled(Box)(({ theme }) => ({
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1.5),
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.border.main}`,
    boxShadow: theme.shadows[1],
}));

const DAY_CHOICES = [
    { value: 0, label: 'شنبه' },
    { value: 1, label: 'یکشنبه' },
    { value: 2, label: 'دوشنبه' },
    { value: 3, label: 'سه‌شنبه' },
    { value: 4, label: 'چهارشنبه' },
    { value: 5, label: 'پنج‌شنبه' },
    { value: 6, label: 'جمعه' },
];

export default function FilterPanel({
    filters,
    onFilterChange,
    onDayChange,
    onCourseChange,
    onClearFilters,
    hasActiveFilters
}) {
    // Fetch courses for checkbox options, narrowed to the selected day chips so the
    // list only offers courses that actually have a class on those days.
    // Memoize params to prevent infinite loops; useFetch refetches when values change.
    const daysKey = (filters.days || []).join(',');
    const coursesParams = useMemo(() => {
        const params = {
            page_size: 100,
            ordering: 'course_name',
            active_enrollments_only: 'true',
        };
        // Mirror buildParams: no chips or all 7 selected means "all days"
        const dayCount = daysKey === '' ? 0 : daysKey.split(',').length;
        if (dayCount > 0 && dayCount !== 7) {
            params.enrollment_days = daysKey;
        }
        return params;
    }, [daysKey]);
    const { data: coursesData, loading: coursesLoading, error: coursesError } = useFetch('/operations/api/v1/teachers-and-courses/', {
        params: coursesParams
    });
    const courses = coursesData?.results || [];

    // Changing the day chips can remove a ticked course from the options; drop such
    // ticks so an invisible course filter doesn't keep emptying the results.
    useEffect(() => {
        if (coursesLoading || coursesError || !coursesData) return;
        const available = new Set((coursesData.results || []).map((c) => c.id));
        const kept = filters.courses.filter((id) => available.has(id));
        if (kept.length !== filters.courses.length) {
            onFilterChange('courses', kept);
        }
    }, [coursesData, coursesLoading, coursesError, filters.courses, onFilterChange]);

    return (
        <FilterBox>
            <Grid container spacing={2}>
                {/* Right column: course, student, remained sessions */}
                <Grid item xs={12} md={6}>
                    <Stack spacing={1.5}>
                        <Box>
                            <Box sx={{ position: 'relative' }}>
                                <Box sx={{ position: 'absolute', top: -10, left: 12, px: 0.5, fontSize: '0.75rem', color: 'text.secondary', backgroundColor: (theme) => theme.palette.background.default }}>
                                    دوره
                                </Box>
                                <Box sx={{ maxHeight: 150, overflowY: 'auto', border: '1px solid', borderColor: 'primary.main', borderRadius: 1, p: 1 }}>
                                    {coursesLoading ? (
                                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                                            <CircularProgress size={20} />
                                        </Box>
                                    ) : coursesError ? (
                                        <Typography variant="body2" color="error" sx={{ textAlign: 'center', py: 2 }}>
                                            خطا در بارگذاری دوره‌ها
                                        </Typography>
                                    ) : courses.length === 0 ? (
                                        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                                            هیچ دوره‌ای یافت نشد
                                        </Typography>
                                    ) : (
                                        <Grid container spacing={1}>
                                            {courses.map((course) => (
                                                <Grid item xs={12} lg={6} key={course.id}>
                                                    <FormControlLabel
                                                        control={
                                                            <Checkbox
                                                                size="small"
                                                                sx={{ py: 0.25 }}
                                                                checked={filters.courses.includes(course.id)}
                                                                onChange={() => onCourseChange(course.id)}
                                                            />
                                                        }
                                                        label={`${course.course_name}${course.teacher ? ` - ${course.teacher.first_name} ${course.teacher.last_name}` : ''}`}
                                                        sx={{
                                                            m: 0,
                                                            mb: 0.5,
                                                            display: 'flex',
                                                            alignItems: 'flex-start',
                                                            '& .MuiFormControlLabel-label': { fontSize: '0.875rem', pt: '2px', textAlign: 'start' },
                                                        }}
                                                    />
                                                </Grid>
                                            ))}
                                        </Grid>
                                    )}
                                </Box>
                            </Box>
                        </Box>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                            <TextField
                                fullWidth
                                size="small"
                                label="هنرجو"
                                value={filters.student}
                                onChange={(e) => onFilterChange('student', e.target.value)}
                            />
                            <TextField
                                fullWidth
                                size="small"
                                type="number"
                                label="جلسات باقی‌مانده (حداکثر)"
                                value={filters.remained_sessions_lte}
                                onChange={(e) => onFilterChange('remained_sessions_lte', e.target.value)}
                                placeholder="مثال: 3"
                            />
                        </Stack>
                    </Stack>
                </Grid>

                {/* Left column: day chips, no-attendance, clear */}
                <Grid item xs={12} md={6}>
                    <Stack spacing={1.5}>
                        <Box>
                            <Stack direction="row" spacing={1.5} useFlexGap flexWrap="wrap">
                                {DAY_CHOICES.map((day) => (
                                    <Chip
                                        key={day.value}
                                        label={day.label}
                                        color={filters.days.includes(day.value) ? 'primary' : 'default'}
                                        variant={filters.days.includes(day.value) ? 'filled' : 'outlined'}
                                        size="small"
                                        onClick={() => onDayChange(day.value)}
                                        sx={{ mb: 1 }}
                                    />
                                ))}
                            </Stack>
                        </Box>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={filters.no_attendance_today}
                                    onChange={(e) => onFilterChange('no_attendance_today', e.target.checked)}
                                    size="small"
                                />
                            }
                            label="هنرجویانی که امروز حضور و غیاب نشدند"
                            sx={{ fontSize: '0.875rem' }}
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={filters.attendance_done_today}
                                    onChange={(e) => onFilterChange('attendance_done_today', e.target.checked)}
                                    size="small"
                                />
                            }
                            label="هنرجویانی که امروز حضور و غیاب شدند"
                            sx={{ fontSize: '0.875rem' }}
                        />
                        <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                            <Button
                                variant="outlined"
                                startIcon={<ClearIcon />}
                                onClick={onClearFilters}
                                disabled={!hasActiveFilters}
                                size="small"
                            >
                                پاک کردن فیلترها
                            </Button>
                        </Box>
                    </Stack>
                </Grid>
            </Grid>
        </FilterBox>
    );
}

